import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API } from '../App';
import { loadLogo } from './BatchReports';

// Employee Compare: every dealer on one game under two benchmarks, side by side.
// A dealer's pace is read as a percentage of the benchmark for the spots per round
// they actually dealt, so the two eras compare fairly even when the tables differed.
// 100% means exactly at the benchmark of the day.

const styles = {
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '24px', marginBottom: '24px' },
  cardTitle: { color: '#ffffff', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' },
  cardSubtitle: { color: '#a0a0b0', fontSize: '13px', marginBottom: '20px', lineHeight: 1.6 },
  filterRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' },
  label: {
    color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase',
    marginBottom: '6px', display: 'block',
  },
  select: {
    padding: '10px 12px', backgroundColor: '#0f3460', border: '1px solid #0f3460',
    borderRadius: '8px', color: '#ffffff', fontSize: '14px', outline: 'none',
    minWidth: '220px', colorScheme: 'dark',
  },
  button: {
    padding: '12px 28px', backgroundColor: '#e94560', border: 'none', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase',
    padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #0f3460', whiteSpace: 'nowrap',
  },
  thGroup: {
    color: '#ffffff', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase',
    padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #0f3460',
    backgroundColor: '#0f3460',
  },
  td: { color: '#ffffff', fontSize: '13px', padding: '10px 12px', borderBottom: '1px solid #0f3460' },
  muted: { color: '#a0a0b0', fontSize: '13px' },
  error: { color: '#e94560', fontSize: '14px', marginBottom: '12px' },
  statBox: {
    backgroundColor: '#0f3460', borderRadius: '10px', padding: '14px 20px', textAlign: 'center',
    minWidth: '130px',
  },
  exportButton: {
    padding: '10px 18px', backgroundColor: '#0f3460', border: '1px solid #1b4b87',
    borderRadius: '8px', color: '#ffffff', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
  },
  statValue: { color: '#ffffff', fontSize: '24px', fontWeight: 'bold' },
  statLabel: { color: '#a0a0b0', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
};

const NONE = 'none';
const SHIFTS = ['Day', 'Swing', 'Grave'];

// One row of the report as plain values, shared by the table, the spreadsheet and
// the PDF so all three always say the same thing.
function rowCells(r) {
  const side = (x) => (x
    ? [x.tests, x.hph != null ? `${x.hph}${x.spr ? ` @ ${x.spr}` : ''}` : '',
       x.pct_of_benchmark != null ? `${x.pct_of_benchmark}%` : '']
    : ['', '', '']);
  return [
    r.payroll_number || '',
    r.name + (r.active ? '' : ' (inactive)'),
    r.shift || 'Day',
    ...side(r.a),
    ...side(r.b),
    r.change != null ? `${r.change > 0 ? '+' : ''}${r.change}` : '',
  ];
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function fileStem(data) {
  const clean = (t) => (t || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  return `employee-compare-${clean(data.game?.name)}-${new Date().toISOString().slice(0, 10)}`;
}

function pctColor(pct) {
  if (pct == null) return '#a0a0b0';
  if (pct >= 100) return '#00cc00';
  if (pct >= 95) return '#ffcc00';
  return '#e94560';
}

function changeColor(c) {
  if (c == null) return '#a0a0b0';
  if (c > 0) return '#00cc00';
  if (c < 0) return '#e94560';
  return '#a0a0b0';
}

function EmployeeCompare() {
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState('');
  const [choices, setChoices] = useState([]);
  const [periodA, setPeriodA] = useState(NONE);
  const [periodB, setPeriodB] = useState(NONE);
  const [shift, setShift] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/game-types`, { headers })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setGames(list);
        if (list.length) setGameId(list[0].id);
      })
      .catch(() => setError('Could not load games'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gameId) return;
    setData(null);
    fetch(`${API}/admin/games/${gameId}/benchmark-periods`, { headers })
      .then(r => r.json())
      .then(d => {
        const list = d.choices || [];
        setChoices(list);
        // Default to the two most recent benchmarks, newest on the right.
        setPeriodB(list[0] ? list[0].id : NONE);
        setPeriodA(list[1] ? list[1].id : NONE);
      })
      .catch(() => setChoices([]));
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function run() {
    setBusy(true);
    setError('');
    try {
      const params = new URLSearchParams({ game_type_id: gameId, period_a: periodA, period_b: periodB });
      if (shift) params.append('shift', shift);
      const res = await fetch(`${API}/admin/reports/employee-compare?${params}`, { headers });
      const d = await res.json();
      if (res.ok) setData(d);
      else setError(typeof d.detail === 'string' ? d.detail : 'Could not build the report');
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  function sideCells(side) {
    if (!side) {
      return (
        <>
          <td style={{ ...styles.td, color: '#a0a0b0' }}>—</td>
          <td style={{ ...styles.td, color: '#a0a0b0' }}>—</td>
          <td style={{ ...styles.td, color: '#a0a0b0' }}>—</td>
        </>
      );
    }
    return (
      <>
        <td style={styles.td}>{side.tests}</td>
        <td style={styles.td}>{side.hph}{side.spr ? ` @ ${side.spr}` : ''}</td>
        <td style={{ ...styles.td, color: pctColor(side.pct_of_benchmark), fontWeight: 'bold' }}>
          {side.pct_of_benchmark != null ? `${side.pct_of_benchmark}%` : '—'}
        </td>
      </>
    );
  }

  const rows = data?.rows || [];

  const HEADINGS = ['Payroll', 'Dealer', 'Shift', 'Tests', 'HPH @ SPR', '% of benchmark',
                    'Tests', 'HPH @ SPR', '% of benchmark', 'Change (pts)'];

  function exportCsv() {
    // A plain CSV, which Excel opens directly. The benchmark each column belongs to
    // is written into the heading so the file makes sense on its own.
    const head = ['Payroll', 'Dealer', 'Shift',
                  `${data.a.label} — Tests`, `${data.a.label} — HPH @ SPR`, `${data.a.label} — % of benchmark`,
                  `${data.b.label} — Tests`, `${data.b.label} — HPH @ SPR`, `${data.b.label} — % of benchmark`,
                  'Change (pts)'];
    const cell = (v) => {
      const t = v == null ? '' : String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const lines = [
      [`Employee Compare — ${data.game?.name}`],
      [`${data.a.label}  vs  ${data.b.label}`],
      [`Shift: ${data.shift || 'All shifts'}`, `Run: ${new Date().toLocaleString()}`],
      [`Tested in both: ${data.summary?.in_both ?? 0}`, `Improved: ${data.summary?.improved ?? 0}`,
       `Declined: ${data.summary?.declined ?? 0}`,
       `Average change: ${data.summary?.average_change != null ? `${data.summary.average_change} pts` : '—'}`],
      [],
      head,
      ...rows.map(rowCells),
    ].map(line => line.map(cell).join(',')).join('\r\n');
    // The byte order mark keeps Excel from mangling accented names.
    downloadBlob(new Blob(['\ufeff' + lines], { type: 'text/csv;charset=utf-8;' }),
                 `${fileStem(data)}.csv`);
  }

  async function exportPdf() {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const tenantName = localStorage.getItem('admin_name') || 'Casino';
    const logo = await loadLogo();

    const header = () => {
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageW, 40, 'F');
      doc.setDrawColor(60, 60, 60);
      doc.setLineWidth(0.6);
      doc.rect(4, 4, pageW - 8, 32, 'S');
      doc.setTextColor(0, 0, 0);
      if (logo && logo.dataUrl) {
        const logoH = 12;
        doc.addImage(logo.dataUrl, 'PNG', 10, 14, logoH * logo.aspect, logoH);
      } else {
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('EZ PACE', 12, 24);
      }
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('Employee Compare', pageW / 2, 16, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${data.game?.name} — ${data.a.label}  vs  ${data.b.label}`, pageW / 2, 24,
               { align: 'center' });
      doc.setFontSize(9);
      doc.text(`Shift: ${data.shift || 'All shifts'}`, pageW / 2, 31, { align: 'center' });
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(tenantName, pageW - 12, 16, { align: 'right' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(new Date().toLocaleDateString(), pageW - 12, 24, { align: 'right' });
    };
    header();

    // Summary band.
    doc.setFillColor(220, 235, 250);
    doc.roundedRect(12, 44, pageW - 24, 14, 2, 2, 'F');
    doc.setDrawColor(100, 150, 200);
    doc.setLineWidth(0.4);
    doc.roundedRect(12, 44, pageW - 24, 14, 2, 2, 'S');
    const su = data.summary || {};
    const cells = [
      ['Tested in both', String(su.in_both ?? 0)],
      ['Improved', String(su.improved ?? 0)],
      ['Declined', String(su.declined ?? 0)],
      ['Average change', su.average_change != null ? `${su.average_change > 0 ? '+' : ''}${su.average_change} pts` : '—'],
    ];
    cells.forEach(([label, value], i) => {
      const x = 12 + ((pageW - 24) / cells.length) * (i + 0.5);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(value, x, 51, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(label, x, 56, { align: 'center' });
    });

    autoTable(doc, {
      startY: 62,
      head: [
        [{ content: '', colSpan: 3 },
         { content: data.a.label, colSpan: 3, styles: { halign: 'center' } },
         { content: data.b.label, colSpan: 3, styles: { halign: 'center' } },
         { content: '' }],
        HEADINGS,
      ],
      body: rows.map(rowCells),
      theme: 'grid',
      margin: { left: 12, right: 12, top: 62 },
      styles: { cellPadding: 1.6, halign: 'center', fontSize: 9,
                lineColor: [110, 110, 110], lineWidth: 0.15 },
      tableLineColor: [110, 110, 110],
      tableLineWidth: 0.3,
      headStyles: { fillColor: [180, 210, 235], textColor: [0, 0, 0], fontStyle: 'bold' },
      bodyStyles: { fillColor: [220, 235, 250], textColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      columnStyles: { 0: { halign: 'left' }, 1: { halign: 'left' } },
      didParseCell: (d) => {
        // Green ahead of the benchmark, red behind it — same reading as on screen.
        if (d.section !== 'body') return;
        const text = String(d.cell.raw ?? '');
        if ((d.column.index === 5 || d.column.index === 8) && text.endsWith('%')) {
          const pct = parseFloat(text);
          d.cell.styles.textColor = pct >= 100 ? [0, 140, 0] : (pct >= 95 ? [170, 130, 0] : [200, 30, 30]);
          d.cell.styles.fontStyle = 'bold';
        }
        if (d.column.index === 9 && text) {
          const c = parseFloat(text);
          d.cell.styles.textColor = c > 0 ? [0, 140, 0] : (c < 0 ? [200, 30, 30] : [0, 0, 0]);
          d.cell.styles.fontStyle = 'bold';
        }
      },
      didDrawPage: (d) => { if (d.pageNumber > 1) header(); },
    });

    const endY = doc.lastAutoTable?.finalY || 62;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(70, 70, 70);
    doc.text('Each test is counted against the benchmark it was measured against at the time. '
             + 'Pace is shown as a percentage of that benchmark for the spots per round dealt, '
             + 'so 100% is exactly at the standard of the day.',
             12, Math.min(endY + 8, doc.internal.pageSize.getHeight() - 8), { maxWidth: pageW - 24 });
    doc.save(`${fileStem(data)}.pdf`);
  }

  return (
    <>
      <div style={styles.card}>
        <div style={styles.cardTitle}>Employee Compare</div>
        <div style={styles.cardSubtitle}>
          Every dealer tested on one game under two benchmarks. Each test is counted against the benchmark it
          was measured against at the time, and a dealer's pace is shown as a percentage of that benchmark for
          the spots per round they dealt — so 100% means exactly at the standard of the day, whichever era it
          came from.
        </div>
        <div style={styles.filterRow}>
          <div>
            <label style={styles.label}>Game</label>
            <select style={styles.select} value={gameId} onChange={e => setGameId(e.target.value)}>
              {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>Earlier benchmark</label>
            <select style={styles.select} value={periodA} onChange={e => setPeriodA(e.target.value)}>
              <option value={NONE}>Before any benchmark</option>
              {choices.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>Later benchmark</label>
            <select style={styles.select} value={periodB} onChange={e => setPeriodB(e.target.value)}>
              <option value={NONE}>Before any benchmark</option>
              {choices.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>Shift</label>
            <select
              style={{ ...styles.select, minWidth: '150px' }}
              value={shift}
              onChange={e => setShift(e.target.value)}
            >
              <option value="">All shifts</option>
              {SHIFTS.map(sh => <option key={sh} value={sh}>{sh}</option>)}
            </select>
          </div>
          <button style={styles.button} onClick={run} disabled={busy || !gameId}>
            {busy ? 'Building...' : 'Run Report'}
          </button>
        </div>
        {choices.length === 0 && (
          <div style={{ ...styles.muted, marginTop: '14px' }}>
            No Benchmark Period has been closed for this game yet, so there is only one era to look at.
            Run one from Games &amp; Benchmarks and this report will compare across them.
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {data && (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={styles.cardTitle}>
                {data.game?.name}: {data.a?.label} → {data.b?.label}
              </div>
              <div style={{ ...styles.muted, marginTop: '4px' }}>
                {data.shift ? `${data.shift} shift` : 'All shifts'} · {rows.length} dealer{rows.length === 1 ? '' : 's'}
              </div>
            </div>
            {rows.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styles.exportButton} onClick={exportCsv}>Export to Excel</button>
                <button style={styles.exportButton} onClick={exportPdf}>Download PDF</button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '16px 0 24px' }}>
            <div style={styles.statBox}>
              <div style={styles.statValue}>{data.summary?.in_both ?? 0}</div>
              <div style={styles.statLabel}>Tested in both</div>
            </div>
            <div style={styles.statBox}>
              <div style={{ ...styles.statValue, color: '#00cc00' }}>{data.summary?.improved ?? 0}</div>
              <div style={styles.statLabel}>Improved</div>
            </div>
            <div style={styles.statBox}>
              <div style={{ ...styles.statValue, color: '#e94560' }}>{data.summary?.declined ?? 0}</div>
              <div style={styles.statLabel}>Declined</div>
            </div>
            <div style={styles.statBox}>
              <div style={{ ...styles.statValue, color: changeColor(data.summary?.average_change) }}>
                {data.summary?.average_change != null
                  ? `${data.summary.average_change > 0 ? '+' : ''}${data.summary.average_change}`
                  : '—'}
              </div>
              <div style={styles.statLabel}>Average change (pts)</div>
            </div>
          </div>

          {rows.length === 0 ? (
            <div style={styles.muted}>No dealers were tested under either benchmark.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} colSpan="3"></th>
                  <th style={styles.thGroup} colSpan="3">{data.a?.label}</th>
                  <th style={styles.thGroup} colSpan="3">{data.b?.label}</th>
                  <th style={styles.th}></th>
                </tr>
                <tr>
                  <th style={styles.th}>Payroll</th>
                  <th style={styles.th}>Dealer</th>
                  <th style={styles.th}>Shift</th>
                  <th style={styles.th}>Tests</th>
                  <th style={styles.th}>HPH @ SPR</th>
                  <th style={styles.th}>% of benchmark</th>
                  <th style={styles.th}>Tests</th>
                  <th style={styles.th}>HPH @ SPR</th>
                  <th style={styles.th}>% of benchmark</th>
                  <th style={styles.th}>Change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.dealer_id}>
                    <td style={styles.td}>{r.payroll_number}</td>
                    <td style={styles.td}>
                      {r.name}
                      {!r.active && <span style={{ color: '#a0a0b0', fontSize: '11px' }}> (inactive)</span>}
                    </td>
                    <td style={{ ...styles.td, color: '#a0a0b0' }}>{r.shift || 'Day'}</td>
                    {sideCells(r.a)}
                    {sideCells(r.b)}
                    <td style={{ ...styles.td, color: changeColor(r.change), fontWeight: 'bold' }}>
                      {r.change != null ? `${r.change > 0 ? '+' : ''}${r.change} pts` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ ...styles.muted, marginTop: '16px', lineHeight: 1.6 }}>
            Change is the difference in percentage points between the two sides. A dealer with tests under only
            one benchmark has no change figure and is listed last.
          </div>
        </div>
      )}
    </>
  );
}

export default EmployeeCompare;
